import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { workspace: string; children: ReactNode };
type State = { error: Error | null };

/**
 * A payload shape the UI did not expect must degrade to a named failure, never to a blank page.
 *
 * This matters more here than in most products: an editor who sees an empty workspace cannot tell
 * "the pipeline produced nothing" from "the dashboard broke", and Art Direction §7 exists precisely
 * to keep those apart. The boundary wraps each workspace rather than the app, so the stream bar and
 * stage nav survive and the editor keeps their orientation.
 */
export class WorkspaceBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[${this.props.workspace}] render failed`, error, info.componentStack);
  }

  override componentDidUpdate(previous: Props): void {
    // Navigating to another workspace must clear the failure, or one bad screen poisons the rest.
    if (previous.workspace !== this.props.workspace && this.state.error) this.setState({ error: null });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="page-medium">
        <div className="absence" data-kind="failed" role="alert">
          <p className="absence__kind">Falló la pantalla</p>
          <p className="absence__what">Esta vista no pudo dibujarse con los datos que devolvió el API.</p>
          <p className="absence__why">
            No significa que el pipeline no haya producido nada — significa que el panel encontró una forma de dato que
            no esperaba.
          </p>
          <p className="absence__change">{error.message}</p>
        </div>
      </div>
    );
  }
}
